import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Eye, FileText } from "lucide-react";
import { Logo } from "@/components/shell/Logo";
import { api, messageErreur } from "@/lib/api";

const Bloc = ({ titre, testId, children }) => (
  <section className="at-card p-5 space-y-3" data-testid={testId}>
    <h2 className="text-base md:text-lg font-bold">{titre}</h2>
    {children}
  </section>
);

const Pastilles = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {items.map((x) => <span key={x} className="at-pill px-3 py-1 text-xs font-semibold" style={{ background: "var(--bg)" }}>{x}</span>)}
  </div>
);

const Processus = ({ p }) => (
  <div className="space-y-2">
    <p className="font-semibold">{p.nom} <span className="at-muted text-sm">· {p.type}</span></p>
    <ol className="space-y-1.5">
      {p.etapes.map((e, i) => (
        <li key={`${e.nom}-${i}`} className="at-fiche px-3 py-2 text-sm flex justify-between gap-2" style={{ borderLeft: e.sortie ? "3px solid var(--danger)" : "3px solid var(--accent)" }}>
          <span className="min-w-0"><b>{i + 1}.</b> {e.nom}</span>
          <span className="at-muted shrink-0">{e.duree_max_heures ? `${e.duree_max_heures} h` : "—"}</span>
        </li>
      ))}
    </ol>
  </div>
);

const Liste = ({ items }) => (
  <ul className="space-y-2 text-sm">
    {items.map(([t, d], i) => <li key={`${t}-${i}`}><b>{t}</b>{d ? <span className="at-muted"> — {d}</span> : null}</li>)}
  </ul>
);

const Contenu = ({ d, texte }) => (
  <>
    <div className="space-y-2">
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight" data-testid="share-title">{d.application}</h1>
      <p className="at-muted">{d.description}</p>
      <a href={texte} target="_blank" rel="noreferrer" className="at-btn at-btn-primary w-full sm:w-auto" data-testid="share-text-version-link"><FileText size={17} />Version texte pour IA</a>
    </div>
    <Bloc titre="Chiffres globaux" testId="share-section-chiffres">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {Object.entries(d.chiffres).map(([k, v]) => <div key={k} className="at-fiche p-3"><p className="text-2xl font-bold">{v}</p><p className="text-xs at-muted">{k}</p></div>)}
      </div>
    </Bloc>
    <Bloc titre="Principes" testId="share-section-principes"><Liste items={d.principes.map((p) => [p])} /></Bloc>
    <Bloc titre="Modules" testId="share-section-modules"><Liste items={d.modules.map((m) => [m.nom, m.description])} /></Bloc>
    <Bloc titre="Profils et navigation" testId="share-section-profils">
      {d.profils.map((r) => <div key={r.code} className="space-y-1.5"><p className="font-semibold">{r.nom}</p><Pastilles items={r.onglets} /></div>)}
    </Bloc>
    <Bloc titre="Processus" testId="share-section-processus">{d.processus.map((p, i) => <Processus key={`${p.nom}-${i}`} p={p} />)}</Bloc>
    <Bloc titre="Vues" testId="share-section-vues"><Liste items={d.vues.map((v) => [`${v.lettre || ""} ${v.nom}`, v.description])} /></Bloc>
    <Bloc titre="Statuts d'appel" testId="share-section-statuts"><Pastilles items={d.statuts_appel.map((s) => s.nom)} /></Bloc>
    <Bloc titre="Pièces par opération" testId="share-section-pieces"><Liste items={d.pieces_par_operation.map((p) => [p.operation, p.pieces.join(", ")])} /></Bloc>
    <Bloc titre="Mini-applications" testId="share-section-outils"><Liste items={d.mini_applications.map((o) => [o.nom, o.description])} /></Bloc>
    <Bloc titre="Connecteurs" testId="share-section-connecteurs"><Liste items={d.connecteurs.map((c) => [`${c.nom} · ${c.mode}`, c.description])} /></Bloc>
    <Bloc titre={`Actions métier (${d.actions.length})`} testId="share-section-actions">
      <details><summary className="cursor-pointer font-semibold text-sm">Afficher la liste</summary><div className="mt-3"><Liste items={d.actions.map((a) => [a.nom, a.description])} /></div></details>
    </Bloc>
  </>
);

export default function PartageLecture() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState("");
  useEffect(() => { api.get(`/partage/public/${token}`).then((r) => setD(r.data)).catch((e) => setErreur(messageErreur(e))); }, [token]);
  const texte = `${process.env.REACT_APP_BACKEND_URL}/api/partage/public/${token}/texte`;
  return (
    <div className="min-h-screen px-4 py-6 max-w-3xl mx-auto space-y-4" data-testid="page-partage">
      <div className="flex items-center justify-between gap-3">
        <Logo />
        <span className="at-pill px-3 py-1 text-xs font-semibold flex items-center gap-1" style={{ background: "var(--surface)" }} data-testid="share-readonly-badge"><Eye size={14} />Lecture seule</span>
      </div>
      {erreur && <p className="at-card p-5 font-semibold" style={{ color: "var(--danger)" }} data-testid="share-error">{erreur}</p>}
      {!d && !erreur && <p className="at-muted" data-testid="share-loading">Chargement…</p>}
      {d && <Contenu d={d} texte={texte} />}
    </div>
  );
}
